// import { idGenerator } from "../../../../CodesThatsGottaGoSomewhereElse";
// import { Transformation2 } from "./Transformation";

import { roundRadians } from '../../../../platform/geom/Math';
import { Matrix } from '../../../../platform/geom/Matrix';
import { Point } from '../../../../platform/geom/Point';
import { resolve } from '../../../Container';
import { place } from '../../commands/OrderCommands';
import { EditorSession } from '../../EditorSession';
import { Figure, Physical, RelativeTo, TransformScope } from '../../entities/Physical';
import { Physicals } from '../../entities/Physicals';
import { Selection } from '../../Selection';
import { SelectionTransformation } from './SelectionTransformation';

// const nextId = idGenerator();

export interface SelectionRotateTransformationConfig {
    origin: Point;
    axis: Point;
}

export class SelectionRotateTransformation extends SelectionTransformation {

    @resolve(EditorSession) private session: EditorSession;

    public readonly type = 'rotate';
    public angle: number = 0;
    public axis: Point = Point.x;

    private origin: Point;
    private baseValues = new Map<Physical, Figure>();
    private subjects: Physical[] = [];
    private initialRotation = 0;
    private snappingOffset1 = 0;
    private snappingOffset15 = 0;

    public configure(config: SelectionRotateTransformationConfig) {
        this.origin = config.origin;
        this.axis = config.axis;
        return this;
    }

    protected initialize(): void {
        this.subjects = Physicals.topToBottom(Physicals.top(this.session.get(Selection)));
        this.baseValues = Figure.fromManyAsMap(this.subjects);

        // TODO: This will need to change if we support a net over multiple objects that is pre-rotated
        if (this.subjects.length === 1) {
            // We want to snap the current selection rotation to the nearest degree, but objects might
            // already be a bit off so work out the offset
            const actual = this.initialRotation = this.subjects[0].rotation(RelativeTo.Parent);
            this.snappingOffset1 = roundRadians(actual) - actual;
            this.snappingOffset15 = roundRadians(actual, 15) - actual;
        }
    }

    protected apply(): void {

        const shouldRoundTo15 = this.latest.modifiers.shift;

        // Convert positions into vectors about origin
        const initial = (this.initial.position.subtract(this.origin));
        const latest = (this.latest.position.subtract(this.origin));

        const rotation = roundRadians(latest.angle(initial), shouldRoundTo15 ? 15 : 1);
        const offset = shouldRoundTo15 ? this.snappingOffset15 : this.snappingOffset1;

        const transform = Matrix.identity
            .translate(this.origin)
            .rotate(rotation)
            .rotate(offset)
            .translate(this.origin.multiply(-1));

        this.angle = this.initialRotation + rotation + offset;
        this.update(() => this.applyTransform(transform));
    }

    protected commit(): void {
        this.update(u => {
            this.dispatch(place, [this.subjects.map(x => x.id)]);
            // Use noop just to be sure changes were made
            u.noop();
        });
    }

    private applyTransform(transform: Matrix) {
        const elmts = Array.from(this.baseValues.keys());
        const scope = new TransformScope(elmts, 'rotate');
        for (const e of elmts) {
            const from = this.baseValues.get(e);
            const to = transform.apply(from);
            e.applyTransformation(scope, from, to);
        }
    }
}
